import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../../API/axiosNeatex';
import type { ArTransactionsModels, ArTransactionsByIdModels } from './ArTransactionsModels';
import type { APIResponse } from '../../../Shared/shared';
export type ZatcaAction = 'sync' | 'send' | 'refresh';

export const addArTransactions = createAsyncThunk(
  'ArTransactions/addArTransactions',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<any>>('Ar/AddArTransactions', item);
      if (data?.succeeded) {
        return data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatArTransactions = createAsyncThunk(
  'ArTransactions/updatArTransactions',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<any>>('Ar/UpdatArTransactions', item);
      if (data?.succeeded) {
        return data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const addArReceiptSchedules = createAsyncThunk(
  'ArTransactions/addArReceiptSchedules',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<any>>('Ar/AddArReceiptSchedules', item);
      if (data?.succeeded) {
        return data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getArTransactionsList = createAsyncThunk(
  'ArTransactions/getArTransactionsList',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<ArTransactionsModels[]>>('Ar/GetArTransactionsList');
      if (data?.succeeded) {
        return data?.data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getArTransactionsById = createAsyncThunk(
  'ArTransactions/getArTransactionsById',
  async (Id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<ArTransactionsByIdModels>>(`Ar/GetArTransactionsById?Id=${Id}`);
      if (data?.succeeded) {
        return data?.data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const TransactionsCreateAccount = createAsyncThunk(
  'ArTransactions/TransactionsCreateAccount',
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<any>>('Ar/InvoicesCreateAccount', item);
      if (data?.succeeded) {
        return data;
      } else {
        return rejectWithValue(data.message);
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const callArZatca = createAsyncThunk(
  'ArTransactions/callArZatca',
  async (args: { id: string; action: ZatcaAction }, thunkAPI) => {
    const { id, action } = args;

    const url =
      action === 'sync' ? `Ar/ZatcaSync/${id}` : action === 'send' ? `Ar/ZatcaSend/${id}` : `Ar/ZatcaRefresh/${id}`;

    try {
      const res = await ntxAPI.post(url);

      const succeeded = res?.data?.succeeded ?? res?.data?.Succeeded ?? true;
      const message = res?.data?.message ?? res?.data?.Message ?? '';

      if (!succeeded) {
        return thunkAPI.rejectWithValue(message || 'ZATCA call failed');
      }

      // ✅ بعد النجاح: نعيد تحميل الفاتورة لتحديث zatcaStatus/zatcaDocType
      await thunkAPI.dispatch(getArTransactionsById(id));

      return { action, succeeded, message, raw: res.data };
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.Message || e?.message || 'Network error';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

const initialState: {
  ArTransactionsList: ArTransactionsModels[];
  ArTransactionsById: ArTransactionsByIdModels;
  loading: boolean;
  error: any | null;
  //
  zatcaBusy: any | null;
  zatcaError: any | null;
  zatcaLastMessage: any | null;
  zatcaLastAction: any | null;
} = {
  ArTransactionsList: [],
  ArTransactionsById: {} as ArTransactionsByIdModels,
  loading: false,
  error: null,
  zatcaBusy: null as null | ZatcaAction,
  zatcaError: null as string | null,
  zatcaLastMessage: null as string | null,
  zatcaLastAction: null as null | ZatcaAction
};

const ArReceiptsSlice = createSlice({
  name: 'ArTransactions',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder

      .addCase(getArTransactionsList.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getArTransactionsList.fulfilled, (state, action: PayloadAction<ArTransactionsModels[]>) => {
        state.loading = false;
        state.ArTransactionsList = action.payload;
      })
      .addCase(getArTransactionsList.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getArTransactionsById.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(getArTransactionsById.fulfilled, (state, action: PayloadAction<ArTransactionsByIdModels>) => {
        state.loading = false;
        state.ArTransactionsById = action.payload;
      })
      .addCase(getArTransactionsById.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      ///////////////////////////////
      .addCase(callArZatca.pending, (state, action) => {
        state.zatcaBusy = action.meta.arg.action;
        state.zatcaError = null;
        state.zatcaLastMessage = null;
        state.zatcaLastAction = action.meta.arg.action;
      })
      .addCase(callArZatca.fulfilled, (state, action) => {
        state.zatcaBusy = null;
        state.zatcaError = null;
        state.zatcaLastMessage = action.payload?.message ?? null;
      })
      .addCase(callArZatca.rejected, (state, action) => {
        state.zatcaBusy = null;
        state.zatcaError = (action.payload as string) || action.error.message || 'ZATCA error';
      });
  }
});

export default ArReceiptsSlice.reducer;
